import React, { useState, useEffect } from "react";
import { getAllObligations, calculateRiskScore } from "../../services/obligationService";
import { useApp } from "../../contexts/AppContext";

const getStatGradient = (color) => {
  if (color === "#c9a84c") return "from-blue-600";
  if (color === "#107c10") return "from-green-700";
  if (color === "#ca5010") return "from-orange-600";
  if (color === "#a4262c") return "from-red-700";
  if (color === "#0078d4") return "from-blue-600";
  return "from-gray-300";
};

const StatCard = ({ label, value, icon, color }) => (
  <div className={`bg-blue-50 border border-gray-200 rounded-lg p-6 relative overflow-hidden`}>
    <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${getStatGradient(color)} to-transparent`} />
    <div className="flex items-start justify-between">
      <div>
        <div className="text-base text-gray-700 mb-2">{label}</div>
        <div className="text-4xl font-serif font-semibold text-gray-900 leading-none">{value}</div>
      </div>
      <div className="text-2xl opacity-60">{icon}</div>
    </div>
  </div>
);

export const ObligationsDashboard = () => {
  const { service } = useApp();
  const [obligations, setObligations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadObligations = async () => {
      try {
        const data = await getAllObligations(service);
        setObligations(data);
      } catch (e) {
        console.error("Error loading obligations:", e);
        setObligations([]);
      } finally {
        setLoading(false);
      }
    };

    loadObligations();
  }, [service]);

  if (loading) {
    return <div className="p-6 text-center text-gray-700">Loading obligations...</div>;
  }

  const today = new Date();
  const stats = {
    total: obligations.length,
    onTrack: obligations.filter(o => o.Status === "On Track").length,
    atRisk: obligations.filter(o => o.Status === "At Risk").length,
    overdue: obligations.filter(o => o.Status === "Overdue").length,
    upcomingThisWeek: obligations.filter(o => {
      const dueDate = new Date(o.DueDate);
      const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
      return daysUntilDue > 0 && daysUntilDue <= 7 && o.Status !== "Completed";
    }).length,
  };

  // Top at-risk obligations
  const atRiskObligations = obligations
    .filter(o => o.Status === "At Risk" || o.Status === "Overdue")
    .map(o => ({ ...o, riskScore: calculateRiskScore(o) }))
    .sort((a, b) => b.riskScore.score - a.riskScore.score)
    .slice(0, 5);

  return (
    <div>
      <div className="mb-5">
        <h2 className="font-serif text-2xl font-medium text-gray-900 mb-1">Contract Obligations</h2>
        <p className="text-gray-700 m-0 text-sm">Monitoring {stats.total} active obligations across all contracts</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total" value={stats.total} icon="📋" color="#c9a84c" />
        <StatCard label="On Track" value={stats.onTrack} icon="✅" color="#107c10" />
        <StatCard label="At Risk" value={stats.atRisk} icon="⚠️" color="#ca5010" />
        <StatCard label="Overdue" value={stats.overdue} icon="🔴" color="#a4262c" />
        <StatCard label="Due This Week" value={stats.upcomingThisWeek} icon="📅" color="#0078d4" />
      </div>

      {/* At-Risk Obligations */}
      {atRiskObligations.length > 0 && (
        <div className="bg-blue-50 border border-gray-200 rounded-lg p-6">
          <div className="font-serif text-lg font-medium mb-4 text-orange-600">
            🚨 At-Risk & Overdue ({atRiskObligations.length})
          </div>
          <div className="flex flex-col gap-3">
            {atRiskObligations.map((o, i) => {
              const dueDate = new Date(o.DueDate);
              const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
              return (
                <div
                  key={i}
                  className={`p-3 bg-white rounded-lg border-l-4 ${
                    o.Status === "Overdue" ? "border-red-300" : "border-orange-300"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-gray-900 mb-1">{o.Title}</div>
                      <div className="text-xs text-gray-700 mb-1">
                        {o.ResponsibleParty} • {daysUntilDue > 0 ? "Due in " + daysUntilDue + "d" : "Overdue " + Math.abs(daysUntilDue) + "d"}
                      </div>
                      <div className="text-xs text-gray-700">Progress: {o.ProgressPercentage}%</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`text-lg font-semibold ${
                        o.riskScore.score >= 70 ? "text-red-700" : "text-orange-600"
                      }`}>{o.riskScore.score}</div>
                      <div className="text-xs text-gray-700">SCORE</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {obligations.length === 0 && (
        <div className="text-center p-10 bg-blue-50 border border-gray-200 rounded-lg text-gray-700">
          <div className="text-3xl mb-2">📋</div>
          <div className="text-base">No obligations created yet.</div>
          <div className="text-xs text-gray-700 mt-1">Create obligations from the contract detail page to track responsibilities and milestones.</div>
        </div>
      )}
    </div>
  );
};
